import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { optimizeAvatar, getOptimizedExt } from "@/lib/mediaOptimizer";
import { useAuth } from "@/contexts/AuthContext";
import { PostWithProfile } from "@/hooks/useFeed";

const PAGE_SIZE = 10;

export interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  bio: string | null;
  location: string | null;
  website: string | null;
  is_verified: boolean | null;
  followers_count: number;
  following_count: number;
  created_at: string;
  birthday: string | null;
}

export const useProfile = (userId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["profile", userId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, user_id, username, display_name, avatar_url, cover_url, bio, location, website, is_verified, followers_count, following_count, created_at")
        .eq("user_id", userId!)
        .single();
      if (error) throw error;
      let birthday: string | null = null;
      if (user?.id && user.id === userId) {
        const { data: bd } = await supabase.rpc("get_my_birthday" as any);
        birthday = (bd as any) ?? null;
      }
      return { ...(data as any), birthday } as Profile;
    },
    enabled: !!userId,
  });
};

export const useCheckDisplayNameAvailable = () => {
  return useMutation({
    mutationFn: async ({ displayName, currentUserId }: { displayName: string; currentUserId: string }) => {
      const trimmed = displayName.trim();
      if (!trimmed) return true;
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, is_verified")
        .ilike("display_name", trimmed)
        .eq("is_verified", true)
        .neq("user_id", currentUserId)
        .limit(1);
      if (error) throw error;
      return !data || data.length === 0; // true = available
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (updates: { display_name?: string; bio?: string; location?: string; website?: string; avatar_url?: string; cover_url?: string; username?: string; birthday?: string | null }) => {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("user_id", user!.id);
      if (error) {
        if (error.message?.includes("unique_verified_display_name")) {
          throw new Error("This display name is already taken by a verified user. Please choose another.");
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
    },
  });
};

export const useUploadAvatar = () => {
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (file: File) => {
      const optimized = await optimizeAvatar(file);
      const ext = getOptimizedExt(optimized);
      const path = `${user!.id}/avatar.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, optimized, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
      return `${urlData.publicUrl}?t=${Date.now()}`;
    },
  });
};

export const useUserPosts = (userId: string | undefined) => {
  return useInfiniteQuery({
    queryKey: ["user-posts", userId],
    queryFn: async ({ pageParam = 0 }) => {
      const { data: posts, error } = await supabase
        .from("posts")
        .select("*")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false })
        .range(pageParam, pageParam + PAGE_SIZE - 1);
      if (error) throw error;
      if (!posts || posts.length === 0) return [] as PostWithProfile[];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url, username, is_verified")
        .eq("user_id", userId!);

      const profile = profiles?.[0] ?? null;
      return posts.map((post) => ({ ...post, profiles: profile })) as PostWithProfile[];
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    initialPageParam: 0,
    enabled: !!userId,
  });
};

export const useIsFollowing = (targetUserId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is-following", user?.id, targetUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("follows")
        .select("id")
        .eq("follower_id", user!.id)
        .eq("following_id", targetUserId!)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!user && !!targetUserId && user.id !== targetUserId,
  });
};

export const useToggleFollow = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ targetUserId, isFollowing }: { targetUserId: string; isFollowing: boolean }) => {
      if (isFollowing) {
        const { error } = await supabase.from("follows").delete().eq("follower_id", user!.id).eq("following_id", targetUserId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("follows").insert({ follower_id: user!.id, following_id: targetUserId });
        if (error) throw error;
      }
    },
    onSuccess: (_, { targetUserId }) => {
      queryClient.invalidateQueries({ queryKey: ["is-following"] });
      queryClient.invalidateQueries({ queryKey: ["profile", targetUserId] });
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
    },
  });
};

export const useFollowersList = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["followers-list", userId],
    queryFn: async () => {
      const { data: follows, error } = await supabase
        .from("follows")
        .select("follower_id")
        .eq("following_id", userId!);
      if (error) throw error;
      if (!follows || follows.length === 0) return [];

      const ids = follows.map((f) => f.follower_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url, username")
        .in("user_id", ids);
      return profiles ?? [];
    },
    enabled: !!userId,
  });
};

export const useFollowingList = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["following-list", userId],
    queryFn: async () => {
      const { data: follows, error } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", userId!);
      if (error) throw error;
      if (!follows || follows.length === 0) return [];

      const ids = follows.map((f) => f.following_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url, username")
        .in("user_id", ids);
      return profiles ?? [];
    },
    enabled: !!userId,
  });
};
